import { useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Tabs, Tab } from '@heyroot/ui/primitives/Tabs';
import { Select } from '@heyroot/ui/primitives/Select';
import { ListPage } from '@heyroot/backoffice-ui';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
  TableScrollRegion,
} from '@heyroot/ui/primitives/Table';
import { useAdminAuth } from '../lib/auth-context.js';
import {
  fetchOnboardingEvents,
  fetchOnboardingProgressEvents,
  queryKeys,
  type OnboardingEventRow,
} from '../lib/api.js';
import { PERMS } from '../../rbac/permissions.js';

interface Props {
  tenantId: string;
}

export function OnboardingEventsPage({ tenantId }: Props) {
  const { hasPerm } = useAdminAuth();

  if (!hasPerm(PERMS.ONBOARDING_AUDIT_VIEW)) {
    return <div className="p-8 text-muted">You don't have access to this page.</div>;
  }

  return <OnboardingHistoryContent key={tenantId} tenantId={tenantId} />;
}

function OnboardingHistoryContent({ tenantId }: Props) {
  return (
    <ListPage
      title="Onboarding history"
      description="Step transitions and admin actions are recorded separately."
      headerChrome="floating"
      breadcrumbs={
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId }}
          search={{ tab: 'onboarding' }}
          className="inline-flex min-h-11 items-center gap-1 text-body-sm text-secondary hover:text-primary"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Onboarding
        </Link>
      }
    >
      <Tabs size="md" variant="pills">
        <Tab label="Step progress">
          <ProgressHistory tenantId={tenantId} />
        </Tab>
        <Tab label="Admin actions">
          <AdminActions tenantId={tenantId} />
        </Tab>
      </Tabs>
    </ListPage>
  );
}

function ProgressHistory({ tenantId }: Props) {
  const [limit, setLimit] = useState(25);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const queryClient = useQueryClient();

  function refreshHistory() {
    void queryClient.invalidateQueries({
      queryKey: ['onboarding-progress-events', tenantId],
      refetchType: 'none',
    });
    // Remount at the first cursor; invalidation makes its cached query refetch.
    setRefreshVersion((version) => version + 1);
  }
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 self-start text-body-sm text-secondary">
          Events per page
          <Select
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
            className="w-auto min-h-11"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </label>
        <Button variant="secondary" size="touch" onClick={refreshHistory}>
          Refresh history
        </Button>
      </div>
      <ProgressHistoryPage key={`${limit}:${refreshVersion}`} tenantId={tenantId} limit={limit} />
    </div>
  );
}

function ProgressHistoryPage({ tenantId, limit }: Props & { limit: number }) {
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);
  const cursor = cursors[page] ?? null;
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['onboarding-progress-events', tenantId, limit, cursor],
    queryFn: () => fetchOnboardingProgressEvents(tenantId, cursor, limit),
    // Keep visited page boundaries stable while paging through this append-only log.
    staleTime: Infinity,
  });

  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy={isPending}>
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isPending ? (
        <p role="status" className="text-body-sm text-muted">
          Loading step progress…
        </p>
      ) : data?.items.length === 0 ? (
        <EmptyState
          title="No step progress recorded"
          description="No step transitions have been recorded for this tenant."
        />
      ) : (
        <TableScrollRegion label="Step progress history">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Timestamp (UTC)</TableHeaderCell>
                <TableHeaderCell>Step</TableHeaderCell>
                <TableHeaderCell>Transition</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell>Actor</TableHeaderCell>
                <TableHeaderCell>Source event</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-caption tabular-nums">
                    <time dateTime={event.occurredAt}>
                      {event.occurredAt.replace('T', ' ').replace('Z', '')}
                    </time>
                  </TableCell>
                  <TableCell>
                    {event.stepLabel && <p className="text-body text-primary">{event.stepLabel}</p>}
                    <span className="font-mono text-caption text-secondary">{event.stepKey}</span>
                  </TableCell>
                  <TableCell>{event.kind}</TableCell>
                  <TableCell>{event.source}</TableCell>
                  <TableCell className="break-all text-secondary">
                    {event.actor ?? 'Not recorded'}
                  </TableCell>
                  <TableCell className="font-mono text-caption break-all">
                    {event.eventId ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScrollRegion>
      )}
      {/* Keyset navigation: no invented total or unstable OFFSET boundaries. */}
      <nav aria-label="Step progress pages" className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="touch"
          disabled={page === 0 || isPending}
          onClick={() => setPage(page - 1)}
        >
          Previous page
        </Button>
        <span role="status" className="text-body-sm text-secondary tabular-nums">
          Page {page + 1}
          {data && !isError ? ` · ${data.items.length} events` : ''}
        </span>
        <Button
          variant="secondary"
          size="touch"
          disabled={!data?.nextCursor || isPending || isError}
          onClick={() => {
            if (!data?.nextCursor) return;
            setCursors((previous) => [...previous.slice(0, page + 1), data.nextCursor]);
            setPage(page + 1);
          }}
        >
          Next page
        </Button>
      </nav>
    </div>
  );
}

function AdminActions({ tenantId }: Props) {
  const [appendedPages, setAppendedPages] = useState<OnboardingEventRow[][]>([]);
  const [appendedCursors, setAppendedCursors] = useState<(string | null)[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.onboardingEvents(tenantId),
    queryFn: () => fetchOnboardingEvents(tenantId),
  });

  const allItems = [...(data?.items ?? []), ...appendedPages.flat()];
  const latestCursor = data?.nextCursor ?? null;
  const nextCursor =
    appendedCursors.length > 0 ? appendedCursors[appendedCursors.length - 1] : latestCursor;

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const res = await fetchOnboardingEvents(tenantId, nextCursor);
      setAppendedPages((prev) => [...prev, res.items]);
      setAppendedCursors((prev) => [...prev, res.nextCursor]);
    } catch (err) {
      setLoadMoreError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, tenantId]);

  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {/* Empty state */}
      {!isPending && !isError && allItems.length === 0 && (
        <EmptyState
          title="No admin actions recorded"
          description="No claims, snoozes or other admin interventions have been recorded for this tenant."
        />
      )}

      {/* Table */}
      {(isPending || allItems.length > 0) && (
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Timestamp</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Admin</TableHeaderCell>
                <TableHeaderCell>Details</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending
                ? Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i} data-testid="skeleton-row">
                      <TableCell
                        className="animate-pulse bg-surface-sunken h-6 rounded-sm"
                        colSpan={4}
                      />
                    </TableRow>
                  ))
                : allItems.map((ev) => (
                    <TableRow key={ev.id}>
                      <TableCell className="font-mono text-caption tabular-nums text-muted whitespace-nowrap">
                        {new Date(ev.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-body-sm">{ev.action}</span>
                      </TableCell>
                      <TableCell className="text-body-sm text-secondary">
                        {ev.adminEmail ?? <span className="text-muted">—</span>}
                      </TableCell>
                      <TableCell className="text-caption text-muted max-w-[300px] truncate">
                        {Object.keys(ev.payload).length > 0 ? JSON.stringify(ev.payload) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Load more */}
      {!isPending && nextCursor && (
        <div className="flex flex-col items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={loadingMore}
            onClick={() => void handleLoadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
          {loadMoreError && <p className="text-caption text-danger-text">{loadMoreError}</p>}
        </div>
      )}
    </div>
  );
}
